import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCheck, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

function timeAgo(iso) {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24); return `${d}d ago`;
}

export function NotificationsBell() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const { selectTenant, tenants } = useTenant();
    const navigate = useNavigate();
    const pollRef = useRef(null);

    const fetchNotes = useCallback(async () => {
        try {
            const res = await notificationsAPI.list(50);
            setItems(res.data.notifications || []);
            setUnread(res.data.unread_count || 0);
        } catch (e) { /* ignore (e.g., not an accountant) */ }
    }, []);

    useEffect(() => {
        fetchNotes();
        pollRef.current = setInterval(fetchNotes, 30000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [fetchNotes]);

    useEffect(() => { if (open) fetchNotes(); }, [open, fetchNotes]);

    const markAll = async () => {
        try {
            await notificationsAPI.markAllRead();
            setItems((prev) => prev.map((i) => ({ ...i, read: true })));
            setUnread(0);
        } catch (e) { /* noop */ }
    };

    const openNote = async (n) => {
        if (!n.read) {
            try { await notificationsAPI.markRead(n.id); } catch (e) { /* noop */ }
            setUnread((u) => Math.max(0, u - 1));
        }
        const full = (tenants || []).find((t) => t.id === n.tenant_id);
        selectTenant(full || { id: n.tenant_id, name: n.tenant_name });
        setOpen(false);
        // Route to where the uploaded item actually lives for the accountant:
        // bank statements -> Bank matching (legacy Reconciliation retired), other docs -> Files, invoices -> Documents.
        const dest = n.type === 'new_bank_statement' ? '/app/bank/matching'
            : n.type === 'new_other_document' ? '/app/files'
            : '/app/documents';
        navigate(dest);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                            {unread > 99 ? '99+' : unread}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="font-medium text-sm">Notifications</span>
                    {unread > 0 && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAll} data-testid="mark-all-read">
                            <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
                        </Button>
                    )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {items.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No notifications yet.</p>
                    ) : items.map((n) => (
                        <button
                            key={n.id}
                            onClick={() => openNote(n)}
                            className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-muted/50 flex gap-2 ${n.read ? 'opacity-60' : ''}`}
                            data-testid={`notification-${n.id}`}
                        >
                            <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="text-sm">
                                    {!n.read && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1 align-middle" />}
                                    <span className="font-medium">{n.actor_name}</span>{' '}
                                    {n.type === 'new_bank_statement' ? 'sent a bank statement:' : n.type === 'new_other_document' ? 'sent a document:' : 'uploaded'}{' '}
                                    <span className="font-medium">{n.filename}</span>
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{n.tenant_name} · {timeAgo(n.created_at)}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
